import { 
  BreadcrumbItem,  
  pushCarsBreadcrumbOnce, 
  getBreadcrumbName,
} from "./sharedBreadcrumbUtils";
import { formatSlugToTitleCase } from "./stringFormatters";
import { filterService } from "@services/filterService";

const findBrandFromSegment = async (
  segment: string,
  separator: string
): Promise<{ foundBrand: string; brandSlug: string; modelName: string }> => {
  try {
    const filterData = await filterService.getFilters();
    const segmentParts = segment.split(separator);
    let foundBrand = '';
    let brandSlug = '';
    let modelName = '';

    if (!filterData?.brand_model) {
      return { foundBrand, brandSlug, modelName };
    }

    for (let i = 1; i <= segmentParts.length; i++) {
      const possibleBrandSlug = segmentParts.slice(0, i).join(separator);

      for (const brandModel of filterData.brand_model) {
        const brandSlugFromValue = brandModel.brand_value?.toLowerCase();
        const directBrandName = brandModel.brand_name.toLowerCase();

        if (
          brandSlugFromValue === possibleBrandSlug.toLowerCase() ||
          directBrandName === possibleBrandSlug.toLowerCase()
        ) {
          foundBrand = brandModel.brand_name;
          brandSlug = brandModel.brand_value || possibleBrandSlug;
          break;
        }
      }

      if (foundBrand) {
        const remainingParts = segmentParts.slice(i).join(separator);
        if (remainingParts) {
          modelName = remainingParts.split(separator).map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
        }
        break;
      }
    }

    return { foundBrand, brandSlug, modelName };
  } catch (error) {
    console.error('Error finding brand from segment:', error);
    return { foundBrand: '', brandSlug: '', modelName: '' };
  }
};

export const generateClientBreadcrumbs = async (path: string): Promise<BreadcrumbItem[]> => {
  const segments = path.split('/').filter(segment => segment);
  const breadcrumbItems: BreadcrumbItem[] = [{ name: "Home", href: "/" }];

  let currentPath = '';

  for (let index = 0; index < segments.length; index++) {
    const segment = segments[index];
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    if (
      index > 0 &&
      segments[index - 1] === 'cars' &&
      (segment.includes('-') || segment.includes('+'))
    ) {
      try {
        const separator = segment.includes('-') ? '-' : '+';
        const { foundBrand, brandSlug, modelName } = await findBrandFromSegment(segment, separator);

        if (foundBrand) {
          pushCarsBreadcrumbOnce(breadcrumbItems);

          breadcrumbItems.push({
            name: foundBrand,
            href: `/collection/${brandSlug}+cars`,
            current: false
          });

          if (modelName) {
            breadcrumbItems.push({
              name: modelName,
              href: currentPath,
              current: isLast
            });
          } else {
            breadcrumbItems[breadcrumbItems.length - 1].current = isLast;
          }
        } else {
          const modelName = segment.split(separator).map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ');
          const dashSplit = segment.split("-");
          const vehicleMake = formatSlugToTitleCase(dashSplit[0]);
          const vehicleModel = formatSlugToTitleCase(dashSplit.slice(1).join("-"));

          pushCarsBreadcrumbOnce(breadcrumbItems);
          breadcrumbItems.push({ name: modelName, vehicleMake: vehicleMake || "", vehicleModel: vehicleModel || "", href: currentPath, current: isLast });
        }
      } catch (error) {
        console.error('Error processing brand-model breadcrumb:', error);
        const separator = segment.includes('-') ? '-' : '+';
        const modelName = segment.split(separator).map(word =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        const dashSplit = segment.split("-");
        const vehicleMake = formatSlugToTitleCase(dashSplit[0]);
        const vehicleModel = formatSlugToTitleCase(dashSplit.slice(1).join("-"));

        pushCarsBreadcrumbOnce(breadcrumbItems);
        breadcrumbItems.push({ name: modelName, vehicleMake: vehicleMake || "", vehicleModel: vehicleModel || "", href: currentPath, current: isLast });
      }
      continue;
    }

    if (segment === 'cars' && index > 0 && segments[index - 1] === 'cars') {
      continue;
    }

    const breadcrumbName = getBreadcrumbName(segment, segments, index);
    if (breadcrumbName) {
      if (segment === 'collection' || segment.includes('collection') || segment === 'compare' || segment.includes('compare')) {
        pushCarsBreadcrumbOnce(breadcrumbItems);
      } else {
        breadcrumbItems.push({
          name: breadcrumbName,
          href: currentPath,
          current: isLast
        });
      }
    }
  }

  return breadcrumbItems;
};

